// Framer Motion animation variants

// ─── Dashboard load sequence ────────────────────────────────────────────────
// Rules this follows (see DASHBOARD_REDESIGN_PLAN.md Part C):
//  1. Nothing loops. Once the page settles it is completely still — this is a
//     screen people stare at all day, and perpetual motion becomes noise.
//  2. Plays on MOUNT only. Data arriving causes a re-render, not a remount, so
//     the entrance does not replay when figures load or the cache refreshes.
//  3. Whole sequence settles under ~1.2s. Longer reads as slow, not smooth.
//  4. Honour prefers-reduced-motion — use framer-motion's own useReducedMotion
//     hook and swap in `noMotionVariants` below.
export const dashboardGridVariants = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.04, delayChildren: 0.02 },
  },
};

export const dashboardItemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.26, ease: [0.22, 0.61, 0.36, 1] },
  },
};

// Drop-in replacement when the user has asked for reduced motion: everything
// renders in its final state immediately, no movement at all.
export const noMotionVariants = {
  initial: { opacity: 1, y: 0 },
  animate: { opacity: 1, y: 0, transition: { duration: 0 } },
};


// Page/Container animations
export const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3
    }
  }
};

// Stagger children animation
export const staggerContainerVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

// Card animations
export const cardVariants = {
  initial: { opacity: 0, y: 30, scale: 0.95 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  hover: {
    y: -4,
    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
    transition: {
      duration: 0.2
    }
  },
  tap: {
    scale: 0.98
  }
};

// Fade in from left
export const fadeInLeftVariants = {
  initial: { opacity: 0, x: -30 },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut'
    }
  }
};

// Fade in from right
export const fadeInRightVariants = {
  initial: { opacity: 0, x: 30 },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut'
    }
  }
};

// Scale up animation
export const scaleUpVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  }
};

// Pulse animation for alerts
export const pulseVariants = {
  animate: {
    scale: [1, 1.05, 1],
    boxShadow: [
      '0 0 0 0 rgba(220, 53, 69, 0)',
      '0 0 0 8px rgba(220, 53, 69, 0.3)',
      '0 0 0 0 rgba(220, 53, 69, 0)'
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      repeatType: 'loop'
    }
  }
};

// Slide up for list items
export const listItemVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3
    }
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.2
    }
  }
};

// Progress bar animation
export const progressVariants = {
  initial: { width: 0 },
  animate: (custom) => ({
    width: `${custom}%`,
    transition: {
      duration: 1,
      ease: 'easeOut',
      delay: 0.3
    }
  })
};

// Number counter spring animation
export const numberVariants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 10
    }
  }
};

// Notification bell shake
export const bellShakeVariants = {
  animate: {
    rotate: [0, 15, -15, 10, -10, 5, -5, 0],
    transition: {
      duration: 0.6,
      ease: 'easeInOut'
    }
  }
};

// Drawer/Panel slide animation
export const drawerVariants = {
  initial: { x: '100%', opacity: 0 },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 200
    }
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};

// Modal/Overlay animation
export const modalOverlayVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

export const modalContentVariants = {
  initial: { opacity: 0, scale: 0.9, y: 20 },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 20,
    transition: { duration: 0.2 }
  }
};

// Skeleton loading shimmer
export const shimmerVariants = {
  animate: {
    backgroundPosition: ['200% 0', '-200% 0'],
    transition: {
      repeat: Infinity,
      duration: 1.5,
      ease: 'linear'
    }
  }
};

// Chart bar animation
export const barVariants = {
  initial: { scaleY: 0, originY: 1 },
  animate: (custom) => ({
    scaleY: 1,
    transition: {
      duration: 0.5,
      delay: custom * 0.1,
      ease: 'easeOut'
    }
  })
};

// Circular progress animation
export const circularProgressVariants = {
  initial: { pathLength: 0, opacity: 0 },
  animate: (custom) => ({
    pathLength: custom / 100,
    opacity: 1,
    transition: {
      pathLength: {
        duration: 1.5,
        ease: 'easeOut',
        delay: 0.2
      },
      opacity: {
        duration: 0.3
      }
    }
  })
};

// Tooltip animation
export const tooltipVariants = {
  initial: { opacity: 0, y: 5, scale: 0.95 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.15
    }
  },
  exit: {
    opacity: 0,
    y: 5,
    scale: 0.95,
    transition: {
      duration: 0.1
    }
  }
};

// Badge pop animation
export const badgeVariants = {
  initial: { scale: 0 },
  animate: {
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 15
    }
  }
};

// Sparkline draw animation
export const sparklineVariants = {
  initial: { pathLength: 0 },
  animate: {
    pathLength: 1,
    transition: {
      duration: 1.5,
      ease: 'easeInOut'
    }
  }
};

// Hover scale for buttons/icons
export const hoverScaleVariants = {
  hover: { scale: 1.1 },
  tap: { scale: 0.95 }
};

// Floating animation for decorative elements
export const floatVariants = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'easeInOut'
    }
  }
};

// Glow pulse for highlights
export const glowVariants = {
  animate: {
    boxShadow: [
      '0 0 5px rgba(128, 0, 32, 0.3)',
      '0 0 20px rgba(128, 0, 32, 0.5)',
      '0 0 5px rgba(128, 0, 32, 0.3)'
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      repeatType: 'loop'
    }
  }
};
